'use client';

import { createClient } from '@/lib/supabase/client';
import { emailService } from '@/lib/services/emailService';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

export interface AdminStats {
  totalQuotes: number;
  pendingQuotes: number;
  totalLeads: number;
  newLeads: number;
  totalModels: number;
  activeModels: number;
}

export const adminService = {
  async checkRoleByUserId(userId: string): Promise<{ isAdmin: boolean; isManager: boolean; isAgent: boolean }> {
    const supabase = createClient();
    try {
      await supabase.auth.getUser();
      const [profileResult, permResult] = await Promise.all([
        supabase.from('user_profiles').select('role').eq('id', userId).single(),
        supabase.from('manager_permissions').select('id').eq('manager_id', userId).limit(1),
      ]);
      const role = profileResult.data?.role;
      const hasManagerPerms = !permResult.error && (permResult.data?.length ?? 0) > 0;
      return {
        isAdmin: role === 'admin',
        isManager: hasManagerPerms || role === 'manager' || role === 'admin',
        isAgent: role === 'agent',
      };
    } catch {
      return { isAdmin: false, isManager: false, isAgent: false };
    }
  },

  async isAdmin(): Promise<boolean> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }

      return data?.role === 'admin';
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async isManager(): Promise<boolean> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const result = await adminService.checkRoleByUserId(user.id);
      return result.isManager;
    } catch (error: any) {
      console.error('Error checking manager status:', error);
      return false;
    }
  },

  async isAgent(): Promise<boolean> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('user_profiles').select('role').eq('id', user.id)
        .single();

      if (error) {
        console.error('Error checking agent status:', error);
        return false;
      }

      return data?.role === 'agent';
    } catch (error: any) {
      console.error('Error checking agent status:', error);
      return false;
    }
  },

  async getStats(): Promise<AdminStats> {
    const supabase = createClient();

    try {
      const [quotesResult, leadsResult, modelsResult] = await Promise.all([
        supabase.from('quotes').select('*', { count: 'exact', head: true }),
        supabase.from('leads').select('*', { count: 'exact', head: true }),
        supabase.from('container_models').select('*', { count: 'exact', head: true }),
      ]);

      const [pendingQuotesResult, newLeadsResult, activeModelsResult] =
        await Promise.all([
          supabase
            .from('quotes').select('*', { count: 'exact', head: true })
            .eq('status', 'pending'),supabase.from('leads').select('*', { count: 'exact', head: true })
            .eq('status', 'new'),supabase.from('container_models').select('*', { count: 'exact', head: true })
            .eq('active', true),
        ]);

      return {
        totalQuotes: quotesResult.count || 0,
        pendingQuotes: pendingQuotesResult.count || 0,
        totalLeads: leadsResult.count || 0,
        newLeads: newLeadsResult.count || 0,
        totalModels: modelsResult.count || 0,
        activeModels: activeModelsResult.count || 0,
      };
    } catch (error: any) {
      console.log('Failed to fetch admin stats:', error.message);
      return {
        totalQuotes: 0,
        pendingQuotes: 0,
        totalLeads: 0,
        newLeads: 0,
        totalModels: 0,
        activeModels: 0,
      };
    }
  },

  async getAllQuotes(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateQuoteStatus(
    quoteId: string,
    status: string,
    quotedPrice?: number,
    notes?: string
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      const updateData: any = { status };
      if (quotedPrice !== undefined) updateData.quoted_price = quotedPrice;
      if (notes !== undefined) updateData.notes = notes;

      const { error } = await supabase
        .from('quotes')
        .update(updateData)
        .eq('id', quoteId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async sendFollowUpEmail(quoteId: string): Promise<{ success: boolean; error?: string }> {
    const { followUpService } = await import('./followUpService');
    return await followUpService.sendFollowUpForQuote(quoteId);
  },

  async runFollowUpReminders(): Promise<{ sent: number; failed: number; error?: string }> {
    const { followUpService } = await import('./followUpService');
    return await followUpService.sendFollowUpReminders();
  },

  async getAllLeads(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getLeadById(leadId: string): Promise<any> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Failed to fetch lead by ID:', error.message);
      return null;
    }
  },

  async getQuotesByLeadEmail(email: string): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('email', email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Failed to fetch quotes by email:', error.message);
      return [];
    }
  },

  async getMessagesByLeadEmail(email: string): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('customer_messages')
        .select('*')
        .eq('customer_email', email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Failed to fetch messages by email:', error.message);
      return [];
    }
  },

  async getContactMessages(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateContactMessageStatus(messageId: string, status: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('contact_messages')
        .update({ status })
        .eq('id', messageId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAssignedLeads(): Promise<any[]> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      // Check user role
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      // Admin sees all leads
      if (profile?.role === 'admin') {
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching all leads:', error);
          return [];
        }
        return data || [];
      }

      // Managers and agents see only their assigned leads
      if (profile?.role === 'manager' || profile?.role === 'agent') {
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .eq('assigned_to', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching assigned leads:', error);
          return [];
        }
        return data || [];
      }

      return [];
    } catch (error: any) {
      console.error('Error fetching assigned leads:', error);
      return [];
    }
  },

  async getAssignedQuotes(): Promise<any[]> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      // Check user role
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      // Admin sees all quotes
      if (profile?.role === 'admin') {
        const { data, error } = await supabase
          .from('quotes')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching all quotes:', error);
          return [];
        }
        return data || [];
      }

      // Managers and agents see only their assigned quotes
      if (profile?.role === 'manager' || profile?.role === 'agent') {
        const { data, error } = await supabase
          .from('quotes')
          .select('*')
          .eq('assigned_to', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching assigned quotes:', error);
          return [];
        }
        return data || [];
      }

      return [];
    } catch (error: any) {
      console.error('Error fetching assigned quotes:', error);
      return [];
    }
  },

  async getAllManagers(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .in('role', ['admin', 'manager'])
        .order('full_name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAllCustomers(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, created_at')
        .eq('role', 'customer')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async assignLeadToManager(leadId: string, managerId: string): Promise<{ success: boolean; managerName?: string; leadName?: string }> {
    const supabase = createClient();

    try {
      // Get lead details
      const { data: lead, error: leadError } = await supabase
        .from('leads')
        .select('email, full_name, phone, business_type, source, status')
        .eq('id', leadId)
        .single();

      if (leadError) {
        console.error('Failed to fetch lead details:', leadError);
        return { success: false };
      }

      // Get assignee (manager/agent) details
      const { data: assignee, error: assigneeError } = await supabase
        .from('user_profiles')
        .select('email, full_name, role')
        .eq('id', managerId)
        .single();

      if (assigneeError) {
        console.error('Failed to fetch assignee details:', assigneeError);
        return { success: false };
      }

      // Update lead assignment
      const { error } = await supabase
        .from('leads')
        .update({ assigned_to: managerId || null })
        .eq('id', leadId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return { success: false };
      }

      // Send email notification to assignee
      if (assignee && lead) {
        const dashboardUrl = assignee.role === 'manager' 
          ? `${typeof window !== 'undefined' ? window.location.origin : ''}/manager-portal`
          : `${typeof window !== 'undefined' ? window.location.origin : ''}/agent-portal`;

        await emailService.sendLeadAssignmentNotification({
          assigneeEmail: assignee.email,
          assigneeName: assignee.full_name || 'Team Member',
          customerName: lead.full_name || 'Unknown',
          customerEmail: lead.email,
          customerPhone: lead.phone || undefined,
          businessType: lead.business_type || undefined,
          leadSource: lead.source,
          leadStatus: lead.status,
          dashboardUrl,
        });
      }

      return { 
        success: true, 
        managerName: assignee?.full_name || 'Manager',
        leadName: lead?.full_name || lead?.email || 'Lead'
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateLeadStatus(leadId: string, status: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('leads')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkUpdateLeadStatus(leadIds: string[], status: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('leads')
        .update({ status })
        .in('id', leadIds);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkAssignLeads(leadIds: string[], managerId: string): Promise<{ success: boolean; managerName?: string; count?: number }> {
    const supabase = createClient();

    try {
      // Get assignee details
      const { data: assignee, error: assigneeError } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('id', managerId)
        .single();

      if (assigneeError) {
        console.error('Failed to fetch assignee details:', assigneeError);
        return { success: false };
      }

      const { error } = await supabase
        .from('leads')
        .update({ assigned_to: managerId || null })
        .in('id', leadIds);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return { success: false };
      }

      return { 
        success: true, 
        managerName: assignee?.full_name || 'Manager',
        count: leadIds.length
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async addManualLead(leadData: {
    full_name: string;
    email: string;
    phone: string;
    business_type?: string;
    source: string;
    notes?: string;
  }): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase.from('leads').insert({
        full_name: leadData.full_name,
        email: leadData.email,
        phone: leadData.phone,
        business_type: leadData.business_type || null,
        source: leadData.source,
        notes: leadData.notes || null,
        status: 'new',
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async markLeadNotificationSent(leadId: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('leads')
        .update({
          notification_sent: true,
          last_contacted_at: new Date().toISOString(),
        })
        .eq('id', leadId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateContainerModel(
    modelId: string,
    updates: {
      name?: string;
      code?: string;
      size?: string;
      dimensions?: string;
      price?: number;
      originalPrice?: number;
      savings?: number;
      featured?: boolean;
      active?: boolean;
      features?: string[];
    }
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.code !== undefined) updateData.code = updates.code;
      if (updates.size !== undefined) updateData.size = updates.size;
      if (updates.dimensions !== undefined) updateData.dimensions = updates.dimensions;
      if (updates.price !== undefined) updateData.price = updates.price;
      if (updates.originalPrice !== undefined)
        updateData.original_price = updates.originalPrice;
      if (updates.savings !== undefined) updateData.savings = updates.savings;
      if (updates.featured !== undefined) updateData.featured = updates.featured;
      if (updates.active !== undefined) updateData.active = updates.active;
      if (updates.features !== undefined) updateData.features = updates.features;

      const { error } = await supabase
        .from('container_models')
        .update(updateData)
        .eq('id', modelId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async createContainer(containerData: {
    name: string;
    code: string;
    size: string;
    dimensions: string;
    price: number;
    originalPrice: number;
    savings: number;
    features: string[];
    featured: boolean;
    active: boolean;
  }): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase.from('container_models').insert({
        name: containerData.name,
        code: containerData.code,
        size: containerData.size,
        dimensions: containerData.dimensions,
        price: containerData.price,
        original_price: containerData.originalPrice,
        savings: containerData.savings,
        features: containerData.features,
        images: [],
        featured: containerData.featured,
        active: containerData.active,
      });

      if (error) {
        console.error('Database error creating container:', error);
        if (isSchemaError(error)) {
          throw new Error(`Schema error: ${error.message}`);
        }
        throw new Error(error.message || 'Failed to create container');
      }

      return true;
    } catch (error: any) {
      console.error('Failed to create container:', error);
      throw error;
    }
  },

  async deleteContainers(containerIds: string[]): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('container_models')
        .delete()
        .in('id', containerIds);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getCustomerInteractions(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('customer_messages')
        .select(`
          *,
          quote:quotes(
            id,
            full_name,
            email,
            container_size,
            status
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.log('Failed to fetch customer interactions:', error.message);
      return [];
    }
  },

  async convertLeadToCustomer(
    leadId: string,
    email: string,
    fullName: string
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      // First, check if user already exists
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', email)
        .single();

      if (existingProfile) {
        // User already exists, just update lead status
        const { error: leadError } = await supabase
          .from('leads')
          .update({ status: 'converted' })
          .eq('id', leadId);

        if (leadError) {
          console.error('Failed to update lead status:', leadError);
          return false;
        }

        return true;
      }

      // Create new user profile (customer role)
      const { data: newProfile, error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          email,
          full_name: fullName,
          role: 'customer',
        })
        .select()
        .single();

      if (profileError) {
        if (isSchemaError(profileError)) {
          console.error('Schema error:', profileError.message);
          throw profileError;
        }
        return false;
      }

      // Update lead status to converted
      const { error: leadError } = await supabase
        .from('leads')
        .update({ status: 'converted' })
        .eq('id', leadId);

      if (leadError) {
        console.error('Failed to update lead status:', leadError);
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Failed to convert lead to customer:', error.message);
      throw error;
    }
  },

  async createManualQuote(quoteData: {
    full_name: string;
    email: string;
    phone: string;
    business_type: string;
    container_size: string;
    location: string;
    timeline: string;
    message?: string;
    quoted_price?: number;
    notes?: string;
  }): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase.from('quotes').insert({
        full_name: quoteData.full_name,
        email: quoteData.email,
        phone: quoteData.phone,
        business_type: quoteData.business_type,
        container_size: quoteData.container_size,
        location: quoteData.location,
        timeline: quoteData.timeline,
        message: quoteData.message || null,
        quoted_price: quoteData.quoted_price || null,
        notes: quoteData.notes || null,
        status: 'pending',
        progress_stage: 'submitted',
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      // Send email notification to customer with quote details
      await emailService.sendAdminCreatedQuoteNotification({
        email: quoteData.email,
        fullName: quoteData.full_name,
        businessType: quoteData.business_type,
        containerSize: quoteData.container_size,
        location: quoteData.location,
        timeline: quoteData.timeline,
        quotedPrice: quoteData.quoted_price,
        notes: quoteData.notes,
      });

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async uploadContainerPhoto(
    file: File,
    modelId: string
  ): Promise<string | null> {
    const supabase = createClient();

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${modelId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('container-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('container-photos').getPublicUrl(filePath);

      return publicUrl;
    } catch (error: any) {
      console.error('Failed to upload photo:', error.message);
      return null;
    }
  },

  async deleteContainerPhoto(photoUrl: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const fileName = photoUrl.split('/').pop();
      if (!fileName) return false;

      const { error } = await supabase.storage
        .from('container-photos')
        .remove([fileName]);

      if (error) throw error;
      return true;
    } catch (error: any) {
      console.error('Failed to delete photo:', error.message);
      return false;
    }
  },

  async updateContainerPhotos(
    modelId: string,
    photos: Array<{ url: string; alt: string }>
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('container_models')
        .update({ images: photos })
        .eq('id', modelId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async createManager(data: {
    email: string;
    fullName: string;
    password: string;
  }): Promise<boolean> {
    const supabase = createClient();

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) return false;

      // Update user profile to admin role
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ role: 'admin', full_name: data.fullName })
        .eq('id', authData.user.id);

      if (profileError) throw profileError;
      return true;
    } catch (error: any) {
      console.error('Failed to create manager:', error.message);
      return false;
    }
  },

  async createAgent(data: {
    email: string;
    fullName: string;
    password: string;
  }): Promise<boolean> {
    const supabase = createClient();

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) return false;

      // Update user profile to agent role
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ role: 'agent', full_name: data.fullName })
        .eq('id', authData.user.id);

      if (profileError) throw profileError;
      return true;
    } catch (error: any) {
      console.error('Failed to create agent:', error.message);
      return false;
    }
  },

  async getAllAgents(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, role, created_at')
        .eq('role', 'agent')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Failed to fetch agents:', error.message);
      return [];
    }
  },

  async getAllCustomerMessages(): Promise<any[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('customer_messages')
        .select(`
          *,
          quotes:quote_id (
            id,
            full_name,
            email,
            container_size,
            status
          ),
          sender:sender_id (
            id,
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Failed to fetch messages:', error.message);
      return [];
    }
  },

  async sendAdminMessage(
    quoteId: string,
    message: string
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase.from('customer_messages').insert({
        quote_id: quoteId,
        sender_id: user.id,
        message_type: 'admin_to_customer',
        message: message,
        read: false,
      });

      if (error) throw error;
      return true;
    } catch (error: any) {
      console.error('Failed to send message:', error.message);
      return false;
    }
  },

  async markMessageAsRead(messageId: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('customer_messages')
        .update({ read: true })
        .eq('id', messageId);

      if (error) throw error;
      return true;
    } catch (error: any) {
      console.error('Failed to mark message as read:', error.message);
      return false;
    }
  },

  async updateAppointmentStatus(appointmentId: string, status: string): Promise<boolean> {
    const supabase = createClient();

    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', appointmentId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        return false;
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};